// Main React app entry point
import React, { useState, useEffect } from 'react';
import { buildApiUrl } from './config/api';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import CoachRegistrationPage from './pages/CoachRegistrationPage';
import PlayerRegistrationPage from './pages/PlayerRegistrationPage';
import ParentRegistrationPage from './pages/ParentRegistrationPage';
import PlayerEvaluationForm from './pages/PlayerEvaluationForm';
import PlayerDashboard from './pages/PlayerDashboard';
import ParentDashboard from './pages/ParentDashboard';
import CoachDashboard from './pages/CoachDashboard';
import AdminPage from './pages/AdminPage';
import AdminChoiceModal from './components/AdminChoiceModal';
import CoachInitiationPage from './pages/CoachInitiationPage';
import SearchPlayerPage from './pages/SearchPlayerPage';
import VideoAnalysisPage from './pages/VideoAnalysisPage';
import VideoKinematicsPage from './pages/VideoKinematicsPage';
import VideoKinematicsHub from './pages/VideoKinematicsHub';
import VideoKinematicsLibrary from './components/VideoKinematicsLibrary';
import VideoKinematicsNewAnalysis from './pages/VideoKinematicsNewAnalysis';
import ConfirmationModal from './components/ConfirmationModal';
import './App.css';

type View = 'landing' | 'login' | 'coach-register' | 'player-register' | 'parent-register' | 'coach-initiation' | 'search-player' | 'coach-dashboard' | 'coach-form' | 'player-dashboard' | 'parent-dashboard' | 'admin' | 'video-analysis' | 'video-kinematics' | 'video-kinematics-hub' | 'video-kinematics-library' | 'video-kinematics-new' | 'video-kinematics-results';

function App() {
  // Note: Portal theme is handled only in LandingPage component
  // Other pages will use regular dark CSS regardless of URL parameter

  // Always start with landing page
  const [currentView, setCurrentView] = useState<View>('landing');
  const [userType, setUserType] = useState<'coaches' | 'players' | 'parents' | null>(null);
  const [showAdminChoice, setShowAdminChoice] = useState(false);
  const [playerId, setPlayerId] = useState<number | null>(null);
  const [evaluationData, setEvaluationData] = useState<any>(null);
  const [viewingPlayerAsCoach, setViewingPlayerAsCoach] = useState(false);
  const [videoKinematicsPlayerId, setVideoKinematicsPlayerId] = useState<number | null>(null);
  const [videoKinematicsData, setVideoKinematicsData] = useState<any>(null);
  const [videoKinematicsAnalysisId, setVideoKinematicsAnalysisId] = useState<number | null>(null);
  const [confirmationModal, setConfirmationModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
    showCancel?: boolean;
  }>({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    showCancel: true,
  });

  const handleGetStarted = (selectedUserType: 'coaches' | 'players' | 'parents') => {
    setUserType(selectedUserType);
    setCurrentView('login');
  };

  const handleBackToLanding = () => {
    setCurrentView('landing');
    setUserType(null);
    setShowAdminChoice(false);
    setPlayerId(null);
  };

  const handleBackToCoachInitiation = () => {
    setCurrentView('coach-initiation');
  };

  const handleBackToLogin = () => {
    // Clear authentication state
    localStorage.removeItem('access_token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('user_role');
    localStorage.removeItem('username');
    localStorage.removeItem('is_admin');
    setCurrentView('login');
  };

  const handleLoginSuccess = (isAdmin?: boolean, playerIdParam?: number) => {
    if (userType === 'coaches' && isAdmin) {
      setShowAdminChoice(true);
    } else if (userType === 'coaches') {
      setCurrentView('coach-initiation');
    } else if (userType === 'players') {
      setPlayerId(playerIdParam || null);
      setCurrentView('player-dashboard');
    } else if (userType === 'parents') {
      setCurrentView('parent-dashboard');
    }
  };

  const handleAdminChoice = (choice: 'admin' | 'form') => {
    setShowAdminChoice(false);
    if (choice === 'admin') {
      setCurrentView('admin');
    } else {
      setCurrentView('coach-dashboard');
    }
  };

  const handleNewEvaluation = () => {
    setEvaluationData(null);
    setCurrentView('coach-form');
  };

  const handleSearchPlayer = () => {
    setCurrentView('search-player');
  };

  const handleCreateNewEvaluation = () => {
    setEvaluationData(null);
    setCurrentView('coach-form');
  };

  const handleEditEvaluation = (evaluationId: number) => {
    setEvaluationData({ evaluation_id: evaluationId });
    setCurrentView('coach-form');
  };

  const handleCreateNewForPlayer = (playerId: number, playerName: string) => {
    // Prefill player info and open form
    setEvaluationData({
      player_id: playerId,
      player_name: playerName,
    });
    setCurrentView('coach-form');
  };

  const handleCreateNewEvaluationForPlayer = (playerName: string, playerAge: number, playerPosition: string) => {
    // Parse player name to get first and last name
    const nameParts = playerName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';
    
    // Prefill player info and open form in "new" mode (not edit)
    setEvaluationData({
      player_name: playerName,
      player_first_name: firstName,
      player_last_name: lastName,
      player_age: playerAge,
      player_position: playerPosition,
      is_new_evaluation: true, // Flag to indicate this is a new evaluation, not an edit
    });
    setCurrentView('coach-form');
  };

  const handleViewPlayerDashboard = (playerId: number) => {
    setPlayerId(playerId);
    setCurrentView('player-dashboard');
  };

  const handleViewPlayerDashboardFromCoach = (playerId: number) => {
    setPlayerId(playerId);
    setViewingPlayerAsCoach(true);
    setCurrentView('player-dashboard');
  };

  const handleBackToCoachDashboard = () => {
    setViewingPlayerAsCoach(false);
    setCurrentView('coach-dashboard');
  };

  const handleSaveSuccess = (message: string) => {
    setConfirmationModal({
      isOpen: true,
      title: 'Success',
      message: message,
      onConfirm: () => {
        setConfirmationModal({ ...confirmationModal, isOpen: false });
        setCurrentView('coach-initiation');
      },
      showCancel: false,
    });
  };

  const handleLoadEvaluation = async (evaluationId: number) => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch(buildApiUrl(`/evaluations/${evaluationId}`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Set evaluation data with the evaluation_id so the form can load it
        setEvaluationData({ 
          evaluation_id: evaluationId,
          evaluation: data.evaluation 
        });
        setCurrentView('coach-form');
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to load evaluation:', errorData.detail || 'Unknown error');
      }
    } catch (err) {
      console.error('Failed to load evaluation: Network error', err);
    }
  };

  const handleViewDashboard = (data: any) => {
    setEvaluationData(data);
    setCurrentView('coach-dashboard');
  };

  const handleRegisterClick = (type: 'coaches' | 'players' | 'parents') => {
    if (type === 'coaches') {
      setCurrentView('coach-register');
    } else if (type === 'players') {
      setCurrentView('player-register');
    } else if (type === 'parents') {
      setCurrentView('parent-register');
    }
  };

  const handleRegistrationSuccess = () => {
    setCurrentView('login');
  };

  // Auto-login from Parent Portal (SSO)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromPortal = urlParams.get('fromPortal');
    const token = urlParams.get('token');
    const playerIdParam = urlParams.get('playerId');
    
    console.log('?? Futeval+ SSO: Checking for auto-login params', { fromPortal, hasToken: !!token, playerIdParam });
    
    // If token is provided from Parent Portal, auto-login
    if (fromPortal === 'true' && token) {
      console.log('?? Futeval+ SSO: Token received from Parent Portal, length:', token.length);
      // Store token
      localStorage.setItem('access_token', token);
      localStorage.setItem('futeval_access_token', token);
      
      // Verify token by trying to fetch user info
      const verifyToken = async () => {
        try {
          const verifyUrl = buildApiUrl('/evaluations/parent/my-children');
          console.log('?? Futeval+ SSO: Verifying token with API:', verifyUrl);
          // Try to get children list to verify token
          const response = await fetch(verifyUrl, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            mode: 'cors',
          });
          
          console.log('?? Futeval+ SSO: Token verification response:', response.status, response.statusText);
          
          if (response.ok) {
            // Token is valid - decode it to get user info
            try {
              const parts = token.split('.');
              if (parts.length === 3) {
                const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
                if (payload.sub) {
                  localStorage.setItem('user_id', payload.sub);
                }
                if (payload.role) {
                  localStorage.setItem('user_role', payload.role);
                }
              }
            } catch (e) {
              console.warn('Could not decode token:', e);
            }
            
            // Auto-navigate to parent dashboard
            console.log('? Futeval+ SSO: Token verified successfully, navigating to parent dashboard');
            setUserType('parents');
            setCurrentView('parent-dashboard');
            
            // Send token to parent window to confirm login
            if (window.self !== window.top) {
              try {
                window.parent.postMessage({
                  type: 'FUTEVAL_TOKEN',
                  token: token
                }, '*');
                console.log('? Futeval+ SSO: Token sent to parent window (auto-login)');
              } catch (e) {
                console.warn('?? Futeval+ SSO: Could not send token to parent:', e);
              }
            }
            
            // Clean up URL params but keep playerId if provided
            const newUrl = window.location.pathname + (playerIdParam ? `?playerId=${playerIdParam}` : '');
            window.history.replaceState({}, '', newUrl);
          } else {
            // Token invalid, clear it and show login
            const errorText = await response.text().catch(() => 'Unknown error');
            console.error('? Futeval+ SSO: Token verification failed:', response.status, errorText);
            localStorage.removeItem('access_token');
            localStorage.removeItem('futeval_access_token');
            setUserType('parents');
            setCurrentView('login');
          }
        } catch (error: any) {
          console.error('? Futeval+ SSO: Error verifying token:', error);
          console.error('? Futeval+ SSO: Error details:', error.message, error.stack);
          // On error, clear token and show login
          localStorage.removeItem('access_token');
          localStorage.removeItem('futeval_access_token');
          setUserType('parents');
          setCurrentView('login');
        }
      };
      
      verifyToken();
    }
  }, []);

  // Listen for token requests from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security (in production, check specific origin)
      if (event.data && event.data.type === 'REQUEST_TOKEN') {
        const token = localStorage.getItem('access_token');
        if (token && window.self !== window.top) {
          try {
            window.parent.postMessage({
              type: 'FUTEVAL_TOKEN',
              token: token
            }, '*');
            console.log('Token sent to parent window (on request)');
          } catch (e) {
            console.warn('Could not send token to parent:', e);
          }
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  return (
    <div className="App">
      {currentView === 'landing' && (
        <LandingPage onGetStarted={handleGetStarted} />
      )}
      {currentView === 'login' && userType ? (
        <LoginPage 
          userType={userType} 
          onBackToLanding={handleBackToLanding}
          onLoginSuccess={handleLoginSuccess}
          onRegisterClick={handleRegisterClick}
        />
      ) : currentView === 'login' && !userType ? (
        // Fallback: if login view is set but no userType, go back to landing
        <LandingPage onGetStarted={handleGetStarted} />
      ) : null}
      {currentView === 'coach-register' && (
        <CoachRegistrationPage
          onBackToLogin={handleBackToLogin}
          onRegistrationSuccess={handleRegistrationSuccess}
        />
      )}
      {currentView === 'player-register' && (
        <PlayerRegistrationPage
          onBackToLogin={handleBackToLogin}
          onRegistrationSuccess={handleRegistrationSuccess}
        />
      )}
      {currentView === 'parent-register' && (
        <ParentRegistrationPage
          onBackToLogin={handleBackToLogin}
          onRegistrationSuccess={handleRegistrationSuccess}
        />
      )}
      {currentView === 'coach-initiation' && (
        <CoachInitiationPage
          onSearchPlayer={handleSearchPlayer}
          onCreateNewEvaluation={handleCreateNewEvaluation}
          onViewMyPlayers={() => {
            setEvaluationData({ initialTab: 'players' });
            setCurrentView('coach-dashboard');
          }}
          onVideoAnalysis={() => setCurrentView('video-analysis')}
          onExit={handleBackToLanding}
        />
      )}
      {currentView === 'video-analysis' && (
        <VideoAnalysisPage
          onBack={() => setCurrentView('coach-initiation')}
        />
      )}
      {currentView === 'search-player' && (
        <SearchPlayerPage
          onBack={() => setCurrentView('coach-initiation')}
          onEdit={handleEditEvaluation}
          onCreateNew={handleCreateNewForPlayer}
          onViewDashboard={handleViewPlayerDashboard}
        />
      )}
      {currentView === 'coach-dashboard' && (
        <CoachDashboard
          onBackToLanding={handleBackToCoachInitiation}
          onExit={handleBackToLogin}
          onNewEvaluation={handleNewEvaluation}
          onLoadEvaluation={handleLoadEvaluation}
          onCreateNewForPlayer={handleCreateNewEvaluationForPlayer}
          onViewPlayerDashboard={handleViewPlayerDashboardFromCoach}
          onViewVideoKinematics={() => setCurrentView('video-kinematics')}
          onViewVideoKinematicsForPlayer={(pid: number) => {
            setVideoKinematicsPlayerId(pid);
            setCurrentView('video-kinematics-hub');
          }}
          initialTab={evaluationData?.initialTab || 'evaluations'}
        />
      )}
      {currentView === 'coach-form' && (
        <PlayerEvaluationForm
          onBackToLanding={handleBackToLanding}
          onViewDashboard={handleViewDashboard}
          onSaveSuccess={handleSaveSuccess}
          evaluationId={evaluationData?.evaluation_id && !evaluationData?.is_new_evaluation ? evaluationData.evaluation_id : null}
          prefillPlayerInfo={evaluationData?.is_new_evaluation ? {
            first_name: evaluationData.player_first_name || evaluationData.player_name?.split(' ')[0] || '',
            last_name: evaluationData.player_last_name || evaluationData.player_name?.split(' ').slice(1).join(' ') || '',
            age: evaluationData.player_age?.toString() || '',
            position: evaluationData.player_position || '',
          } : evaluationData?.player_name ? {
            first_name: evaluationData.player_name.split(' ')[0] || '',
            last_name: evaluationData.player_name.split(' ').slice(1).join(' ') || '',
            age: '',
            position: '',
          } : undefined}
          mode={evaluationData?.evaluation_id && !evaluationData?.is_new_evaluation ? 'edit' : 'new'}
        />
      )}
      {currentView === 'player-dashboard' && (
        <PlayerDashboard 
          onBackToLanding={handleBackToLanding} 
          onExit={handleBackToLogin} 
          onBackToCoachDashboard={viewingPlayerAsCoach ? handleBackToCoachDashboard : undefined}
          playerId={playerId} 
          viewedByCoach={viewingPlayerAsCoach}
        />
      )}
      {currentView === 'parent-dashboard' && (
        <ParentDashboard onBackToLanding={handleBackToLanding} />
      )}
      {currentView === 'admin' && (
        <AdminPage onBackToLanding={handleBackToLanding} />
      )}
      {currentView === 'video-kinematics' && (
        <VideoKinematicsPage 
          onBack={() => setCurrentView('coach-dashboard')} 
        />
      )}
      {currentView === 'video-kinematics-hub' && videoKinematicsPlayerId && (
        <VideoKinematicsHub
          playerId={videoKinematicsPlayerId}
          onBack={() => setCurrentView('coach-dashboard')}
          onCreateNew={() => setCurrentView('video-kinematics-new')}
          onViewLibrary={() => setCurrentView('video-kinematics-library')}
        />
      )}
      {currentView === 'video-kinematics-library' && videoKinematicsPlayerId && (
        <VideoKinematicsLibrary
          playerId={videoKinematicsPlayerId}
          onBack={() => setCurrentView('video-kinematics-hub')}
          onViewAnalysis={(analysisId: number) => {
            setVideoKinematicsAnalysisId(analysisId);
            setVideoKinematicsData(null); // Clear any previous data to force reload
            setCurrentView('video-kinematics-results');
          }}
        />
      )}
      {currentView === 'video-kinematics-new' && videoKinematicsPlayerId && (
        <VideoKinematicsNewAnalysis
          playerId={videoKinematicsPlayerId}
          onBack={() => setCurrentView('video-kinematics-hub')}
          onAnalysisComplete={(data: any) => {
            setVideoKinematicsData(data);
            setVideoKinematicsAnalysisId(null); // New analysis, not saved yet
            setCurrentView('video-kinematics-results');
          }}
        />
      )}
      {currentView === 'video-kinematics-results' && videoKinematicsData && (
        <VideoKinematicsPage
          initialData={videoKinematicsData}
          analysisId={videoKinematicsAnalysisId || undefined}
          playerId={videoKinematicsPlayerId || undefined}
          onBack={() => {
            if (videoKinematicsAnalysisId) {
              setCurrentView('video-kinematics-library');
            } else {
              setCurrentView('video-kinematics-hub');
            }
          }}
        />
      )}
      {currentView === 'video-kinematics-results' && videoKinematicsAnalysisId && !videoKinematicsData && (
        <VideoKinematicsPage
          analysisId={videoKinematicsAnalysisId}
          playerId={videoKinematicsPlayerId || undefined}
          onBack={() => setCurrentView('video-kinematics-library')}
        />
      )}
      
      {showAdminChoice && (
        <AdminChoiceModal
          onSelectAdmin={() => handleAdminChoice('admin')}
          onSelectEvaluationForm={() => handleAdminChoice('form')}
        />
      )}
      
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        title={confirmationModal.title}
        message={confirmationModal.message}
        onConfirm={confirmationModal.onConfirm}
        onCancel={() => setConfirmationModal({ ...confirmationModal, isOpen: false })}
        showCancel={confirmationModal.showCancel}
      />
    </div>
  );
}

export default App;
